
import React, { useState, useEffect } from "react";
import axios from "axios";
import WidgetManager from "../components/widgets/WidgetManager";
import { useSettings } from "../contexts/SettingsContext";
import { useSubscription } from "../hooks/useSubscription";
import { useNotification } from "../contexts/NotificationContext";
import WelcomeModal from "../components/WelcomeModal";
import SetupWizard from "../components/SetupWizard";
import ImportSuccessModal from "../components/ImportSuccessModal";
import { getApiUrl } from "../config/api";

export default function DashboardHome() {
  const { widgetSettings } = useSettings();
  const { hasFeature, getTierName } = useSubscription();
  const { showNotification } = useNotification();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [hasAssociations, setHasAssociations] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [checkingCounts, setCheckingCounts] = useState(false);
  const [dataCounts, setDataCounts] = useState({ entriesCount: 0, resultsCount: 0, membershipsCount: 0 });
  const [userEmail, setUserEmail] = useState('');
  const [widgetRefreshKey, setWidgetRefreshKey] = useState(0);
  const [showImportSuccessModal, setShowImportSuccessModal] = useState(false);
  const [importSyncResults, setImportSyncResults] = useState(null);

  // Check if user has KCIRodeo features
  const hasKcirodeoFeatures = hasFeature('entries_import') || hasFeature('results_import') || hasFeature('memberships_import');
  const isHorsemgmtTier = getTierName() === 'horsemgmt';

  useEffect(() => {
    checkUserAssociations();
  }, []);

  const checkUserAssociations = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Check associations and user settings in parallel
      const [associationsResponse, settingsResponse] = await Promise.all([
        axios.get(getApiUrl('/api/user-associations'), {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(getApiUrl('/api/settings'), {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const associations = associationsResponse.data || [];
      setHasAssociations(associations.length > 0);
      
      // Check if we should show welcome modal from database
      const hasSeenWelcome = settingsResponse.data?.has_seen_welcome || false;
      const hasCheckedFirstTimeImport = settingsResponse.data?.hasCheckedFirstTimeImport || false;
      const shouldShowWelcome = !hasSeenWelcome && hasKcirodeoFeatures && !isHorsemgmtTier;
      
      // If first login and haven't checked for data yet, check counts
      if (shouldShowWelcome && !hasCheckedFirstTimeImport && hasKcirodeoFeatures) {
        await checkDataCounts(settingsResponse.data);
      } else {
        setShowWelcomeModal(shouldShowWelcome);
      }
      
      // Check if entries sync is needed (> 7 days old or never synced)
      if (hasKcirodeoFeatures && !isHorsemgmtTier) {
        checkEntriesSyncStatus(settingsResponse.data);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking user data:', error);
      setIsLoading(false);
    }
  };

  const checkDataCounts = async (settings) => {
    try {
      setCheckingCounts(true);
      const token = localStorage.getItem("token");
      if (!token) {
        setCheckingCounts(false);
        setShowWelcomeModal(true);
        return;
      }

      // Get user email from profile
      const profileResponse = await axios.get(getApiUrl('/api/profile'), {
        headers: { Authorization: `Bearer ${token}` }
      });

      const email = profileResponse.data?.email || profileResponse.data?.user?.email;
      if (!email) {
        console.error('No email found in profile');
        setCheckingCounts(false);
        setShowWelcomeModal(true);
        return;
      }

      setUserEmail(email);

      // Check counts for this email
      const countsResponse = await axios.get(getApiUrl('/api/data-import/check-counts'), {
        params: { email },
        headers: { Authorization: `Bearer ${token}` }
      });

      const counts = countsResponse.data || { entriesCount: 0, resultsCount: 0, membershipsCount: 0 };
      setDataCounts(counts);

      // Mark that we've checked
      await axios.put(getApiUrl('/api/settings'), 
        { hasCheckedFirstTimeImport: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Show WelcomeModal (with counts if available)
      setShowWelcomeModal(true);

      setCheckingCounts(false);
    } catch (error) {
      console.error('Error checking data counts:', error);
      // On error, fall back to WelcomeModal
      setCheckingCounts(false);
      setShowWelcomeModal(true);
    }
  };

  const handleStartSetup = async () => {
    setShowWelcomeModal(false);
    setShowSetupWizard(true);
    // Mark welcome as seen when user starts setup
    await markWelcomeAsSeen();
  };

  const handleSkipWelcome = async () => {
    const success = await markWelcomeAsSeen();
    setShowWelcomeModal(false);
    if (dataCounts.totalCount > 0) {
      showNotification(
        'You can import your data anytime by going to Settings > KCI Rodeo Sync and clicking "Sync Now".',
        'info'
      );
    }
  };

  const handleCloseWelcome = async () => {
    const success = await markWelcomeAsSeen();
    setShowWelcomeModal(false);
  };

  const markWelcomeAsSeen = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error('No token found for updating welcome status');
        return false;
      }

      const response = await axios.put(getApiUrl('/api/settings'), 
        { has_seen_welcome: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return true;
    } catch (error) {
      console.error('❌ Error updating welcome status:', error);
      console.error('Error details:', error.response?.data);
      return false;
    }
  };

  const handleSetupComplete = async (stepData, data) => {
    setShowSetupWizard(false);
    
    // Check if we have sync results from the import
    if (data?.syncResults) {
      setImportSyncResults(data.syncResults);
      setShowImportSuccessModal(true);
    }
    
    // Refresh associations check and ensure welcome is marked as seen
    await checkUserAssociations();
    // Force widgets to refresh by incrementing the key
    setWidgetRefreshKey(prev => prev + 1);
  };
  
  const checkEntriesSyncStatus = async (settings) => {
    try {
      const lastSyncDate = settings?.lastEntriesSyncDate;
      
      // Check if sync is needed (never synced or > 7 days old)
      let needsSync = false;
      if (!lastSyncDate) {
        needsSync = true;
      } else {
        const syncDate = new Date(lastSyncDate);
        const daysSinceSync = (Date.now() - syncDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceSync > 7) {
          needsSync = true;
        }
      }
      
      if (needsSync) {
        // Check if user has confirmed emails
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const emailsResponse = await axios.get(getApiUrl('/api/entries/confirmed-emails'));
            
            const confirmedEmails = emailsResponse.data?.emails || [];
            if (confirmedEmails.length > 0) {
              // User has confirmed emails, show notification to sync
              showNotification(
                'It\'s been more than a week since your last sync. Click "Sync Now" in Settings to check for new entries.',
                'info'
              );
            }
          } catch (error) {
            // Silently fail - don't show error if check fails
            console.error('Error checking confirmed emails:', error);
          }
        }
      }
    } catch (error) {
      // Silently fail - don't show error if check fails
      console.error('Error checking sync status:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full p-3 sm:p-4">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-3 sm:p-4">
      <WidgetManager layout={widgetSettings.layout} key={widgetRefreshKey} />
      
      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleCloseWelcome}
        onStartSetup={handleStartSetup}
        onSkip={handleSkipWelcome}
        hasKcirodeoFeatures={hasKcirodeoFeatures}
        counts={dataCounts}
        userEmail={userEmail}
        isLoading={checkingCounts}
      />

      {/* Setup Wizard Modal */}
      {showSetupWizard && (
        <SetupWizard
          isModal={true}
          onComplete={handleSetupComplete}
          isFirstTimeImport={dataCounts.totalCount > 0}
        />
      )}

      {/* Import Success Modal */}
      <ImportSuccessModal
        isOpen={showImportSuccessModal}
        onClose={() => setShowImportSuccessModal(false)}
        syncResults={importSyncResults}
      />
    </div>
  );
}
